import App, { routes } from '@/App.tsx'
import { flattenRoutes } from '@/lib/router.ts'
import { buildJobPathFromParts } from '@/lib/jobUrl.ts'
import { registerPrerenderJobs } from '@/lib/prerenderJobs.ts'
import type { Job, JobCategory } from '@/data/jobs.ts'
import { createClient } from '@lumi.new/sdk'
import { createHead, UnheadProvider } from '@unhead/react/server'
import { renderToString } from 'react-dom/server'
import { StaticRouter } from 'react-router-dom/server'

// ── Client Lumi dédié au build (droits serveur) ───────────────────────────
// IMPORTANT : au build (contexte Node, hors navigateur), le client `lumi`
// partagé n'a AUCUNE session authentifiée. `recruiter_jobs.list()` part alors
// sans droits et peut renvoyer une liste vide → aucune offre prérendue →
// <title>/og:title génériques pour les crawlers (LinkedIn/Facebook).
//
// On instancie donc, UNIQUEMENT pour le prerender, un client Lumi authentifié
// avec la clé serveur `LUMI_API_KEY` (même approche que la Deno Function
// `notifyCandidatePublished`). Cette clé est injectée dans l'environnement de
// build et lue via `process.env`. Si elle est absente, on retombe sur un
// client non authentifié (best-effort), ce qui sera signalé dans les logs.
function getBuildEnv(key: string): string {
  // @ts-ignore - `process` existe dans le contexte de build Node de Vite.
  const env = (typeof process !== 'undefined' && process.env) ? process.env : {}
  return (env[key] as string | undefined) ?? ''
}

const buildLumi = createClient({
  projectId: getBuildEnv('PROJECT_ID') || 'p443756950150270976',
  apiBaseUrl: getBuildEnv('API_BASE_URL') || 'https://api.lumi.new',
  authOrigin: getBuildEnv('AUTH_ORIGIN') || 'https://auth.lumi.new',
  ...(getBuildEnv('LUMI_API_KEY')
    ? { authorization: `Bearer ${getBuildEnv('LUMI_API_KEY')}` }
    : {}),
})

interface PrerenderData {
  url: string
}

type PrerenderResult = string | {
  html?: string
  head?: {
    lang?: string
    title?: string
    elements?: Set<{
      type: string
      props: Record<string, string>
      children?: string
    }>
  }
  links?: Set<string> | string[]
} | null | undefined

const paths = flattenRoutes(routes)
  .map(route => route.path)

// ── Prerender des offres recruteurs ────────────────────────────────────────
// On lit recruiter_jobs UNE seule fois pour tout le prerender, on peuple le
// cache mémoire (lu synchroniquement par JobDetail) et on construit les URLs
// SEO canoniques de chaque offre afin que vite-prerender-plugin génère un
// fichier HTML statique par annonce, avec le bon <title>/og:title.
//
// La promesse est mémoïsée : seul le premier appel déclenche la requête réseau.
let jobLinksPromise: Promise<string[]> | null = null

async function loadJobLinks(): Promise<string[]> {
  try {
    const hasKey = Boolean(getBuildEnv('LUMI_API_KEY'))
    console.log(`[prerender] Chargement des offres recruiter_jobs pour le prerender... (cle serveur: ${hasKey ? 'presente' : 'ABSENTE'})`)
    const { list } = await buildLumi.entities.recruiter_jobs.list({})
    const all = (list as Array<Record<string, unknown>>) || []
    console.log(`[prerender] recruiter_jobs.list -> ${all.length} offre(s) brute(s) recuperee(s).`)

    const jobs: Job[] = []
    const links: string[] = []

    for (const rj of all) {
      const id = rj._id ? String(rj._id) : ''
      if (!id) continue

      // On ne pré-rend que les offres approuvées (publiquement visibles).
      const validationStatus = rj.validationStatus as string | undefined
      if (validationStatus && validationStatus !== 'approved') continue

      const category = (rj.category as JobCategory) || 'BNSSA'
      const title = (rj.title as string) || 'offre'
      const location = (rj.location as string) || ''
      const department = (rj.department as string) || ''

      // Cache des données pour le rendu synchrone du <Seo> dans JobDetail.
      jobs.push({
        id,
        title,
        employer: (rj.employer as string) || '',
        location,
        department,
        category,
        contractType: (rj.contractType as Job['contractType']) || 'CDI',
        salary: rj.salary as string | undefined,
        hourlyRate: rj.hourlyRate as string | undefined,
        description: (rj.description as string) || '',
        requirements: (rj.requirements as string[]) || [],
        benefits: (rj.benefits as string[]) || [],
        publishedAt: String(rj.publishedAt || rj.createdAt || '').slice(0, 10),
        deadline: rj.deadline as string | undefined,
        logo: rj.logoUrl as string | undefined,
        missionDate: rj.missionDate as string | undefined,
        missionStartTime: rj.missionStartTime as string | undefined,
        missionEndTime: rj.missionEndTime as string | undefined,
        missionHours: rj.missionHours as number | undefined,
        missionPrice: rj.missionPrice as number | undefined,
        activities: rj.activities as string[] | undefined,
      } as Job)

      const path = buildJobPathFromParts({ id, title, location, department, category })
      if (path) links.push(path)
    }

    registerPrerenderJobs(jobs)
    console.log(`[prerender] ${jobs.length} offre(s) approuvee(s) mise(s) en cache, ${links.length} URL(s) d'offre generee(s).`)
    if (links.length > 0) {
      console.log('[prerender] Exemple d\'URL d\'offre generee:', links[0])
    }
    return links
  } catch (e: any) {
    console.error('[prerender] ECHEC du chargement de recruiter_jobs au build:', e?.message || e)
    return []
  }
}

export async function prerender(data: PrerenderData): Promise<PrerenderResult> {
  try {
    // Charge (une seule fois) les offres + peuple le cache de prerender AVANT
    // le rendu, pour que JobDetail dispose du job de façon synchrone.
    if (!jobLinksPromise) jobLinksPromise = loadJobLinks()
    const jobLinks = await jobLinksPromise

    const head = createHead({
      disableDefaults: true,
    })

    const html = renderToString(
      <UnheadProvider value={head}>
        <StaticRouter location={data.url}>
          <App />
        </StaticRouter>
      </UnheadProvider>,
    )

    const { parseLinks } = await import('vite-prerender-plugin/parse')

    const links = [
      ...parseLinks(html),
      ...paths,
      ...jobLinks,
    ]

    const headTags = await head.resolveTags()
    const lang = headTags.find(tag => tag.tag === 'htmlAttrs')?.props.lang
    const title = headTags.find(tag => tag.tag === 'title')?.textContent
    const elements = headTags
      .filter(tag => tag.tag !== 'htmlAttrs' && tag.tag !== 'title')
      .map(tag => ({
        type: tag.tag,
        props: tag.props,
        children: tag.textContent,
      }))

    return {
      html,
      links,
      head: {
        lang,
        title,
        elements: new Set(elements),
      },
    }
  } catch (e: any) {
    console.warn(`Failed to prerender "${data.url}":`, e.message)
    return null
  }
}
